import { useAuthStore } from '@/stores/authStore'
import type { BusinessType, ModuleKey } from '@/types/industry'

/**
 * What kind of shop this is, and what it calls its stock.
 *
 * All of this rides on the session, so it is available on the first render.
 * That is the point: fetching the label separately means every screen paints
 * "Products" and then corrects itself to "Medicines", which reads as a bug
 * even though the end state is right.
 */

export function useIndustry(): BusinessType {
  return useAuthStore((s) => s.session?.tenant.business_type ?? 'other')
}

export function useIsIndustry(...types: BusinessType[]): boolean {
  const current = useIndustry()

  return types.includes(current)
}

/**
 * The shop's own word for what it sells.
 *
 * Falls back to Products/Product rather than rendering undefined — a session
 * from before this shipped has no labels on it, and a nav item reading
 * "undefined" is worse than one reading the generic word.
 */
export function useItemLabel(): { plural: string; singular: string } {
  /*
   * TWO selectors returning STRINGS, not one returning an object.
   *
   * The object version renders once and then crashes the app with "Maximum
   * update depth exceeded". zustand compares what the selector returned with
   * Object.is, so a fresh object literal is a different value every single
   * render — the store reports a change, React re-renders, the selector builds
   * another object, forever.
   *
   * It type-checks, it lints, it builds, and it takes down every screen that
   * calls it. The only way to see it is to open the app.
   */
  const plural = useAuthStore((s) => s.session?.tenant.labels?.item ?? 'Products')
  const singular = useAuthStore((s) => s.session?.tenant.labels?.item_singular ?? 'Product')

  return { plural, singular }
}

/**
 * Whether this shop has switched a module on.
 *
 * COSMETIC, exactly like useCan. A module decides whether a shop is shown
 * something it asked for; it is not a permission and the server does not
 * consult it. Access is capability-gated, which is what RouteGuardTest
 * enforces on every route.
 */
export function useModule(module: ModuleKey): boolean {
  return useAuthStore((s) => s.session?.modules?.[module] === true)
}
